import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatBytes, type ImportProgress } from "@/lib/dataTransfer";
import { formatCount, formatDateTime } from "@/lib/format";
import { StepMarker } from "./Step";

/**
 * O andamento da importação depois da confirmação, nos dois passos do
 * servidor (o backup e a troca), e o resultado: o que foi gravado e apagado,
 * onde ficou o backup e, se algo falhou, o que e onde.
 */
export function ImportProgressList({
  progress,
  running,
}: {
  progress: ImportProgress;
  running: boolean;
}) {
  const backup = progress.backupResult;
  const result = progress.result;
  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      {running ? (
        <p className="text-caption font-semibold text-muted-foreground">
          Pode levar muitos minutos, conforme o tamanho do Storage. Não feche esta aba.
        </p>
      ) : null}
      <ol className="space-y-3">
        <li className="flex items-start gap-2 text-small">
          <StepMarker state={progress.backup} />
          <div className="min-w-0 space-y-2">
            <p>Backup do Firestore e do Storage de agora</p>
            {backup ? (
              <>
                <p className="text-caption text-muted-foreground">
                  {backup.backupFileName} ({formatBytes(backup.backupBytes)}), guardado no Storage
                  em <span className="break-all font-mono">{backup.backupPath}</span>. O download
                  começou sozinho; se o navegador bloqueou, baixe pelo botão.
                  {backup.backupUrlExpiresAt
                    ? ` O link vale até ${formatDateTime(backup.backupUrlExpiresAt)}.`
                    : ""}
                </p>
                <Button asChild size="sm" variant="outline">
                  <a href={backup.backupUrl} target="_blank" rel="noopener noreferrer">
                    <Download /> Baixar o backup
                  </a>
                </Button>
              </>
            ) : null}
          </div>
        </li>
        <li className="flex items-start gap-2 text-small">
          <StepMarker state={progress.apply} />
          <p>Troca dos arquivos do Storage e dos documentos do Firestore pelos do ZIP</p>
        </li>
      </ol>

      {result?.success ? (
        <div className="rounded-md border border-state-ok/40 bg-state-ok/10 p-3 text-small">
          <p className="font-semibold">Importação concluída.</p>
          <p className="mt-1 text-caption text-muted-foreground">
            {formatCount(result.writtenDocuments, "documento gravado", "documentos gravados")} e{" "}
            {formatCount(result.storageWritten, "arquivo gravado", "arquivos gravados")};{" "}
            {formatCount(
              result.storageDeleted,
              "arquivo que não estava no ZIP foi apagado",
              "arquivos que não estavam no ZIP foram apagados",
            )}
            . Coleções apagadas antes:{" "}
            {result.deletedCollections.length > 0 ? result.deletedCollections.join(", ") : "nenhuma"}.
          </p>
          {result.rewrittenBucket ? (
            <p className="mt-1 break-words text-caption text-muted-foreground">
              As URLs do Firestore passaram do bucket {result.rewrittenBucket.from} para o{" "}
              {result.rewrittenBucket.to}.
            </p>
          ) : null}
        </div>
      ) : null}

      {progress.error ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-small">
          <p className="break-words font-semibold text-destructive">{progress.error}</p>
          {result && result.failures.length > 0 ? (
            <ul className="mt-2 space-y-1">
              {result.failures.map((failure) => (
                <li key={failure.path} className="break-words text-caption">
                  <span className="font-mono">{failure.path}</span>: {failure.message}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
